<template>
  <CardSection class="custom-color-picker">
    <div
      v-if="headerText"
      class="custom-color-picker__header"
    >
      <h4 class="custom-color-picker__header-title">{{ headerText }}</h4>
    </div>
    <form
      class="custom-color-picker__form"
      @submit.prevent="saveColor"
    >
      <div class="custom-color-picker__input-wrapper">
        <TextInput
          ref="inputFieldRef"
          v-model="colorPickerState.hexColor"
          :max-length="9"
          :info="hintText"
          class="custom-color-picker__input"
          placeholder="Enter HEX Code"
          show-detail
          @update:model-value="validateHexColor"
        >
          <template #left-icon>
            <ColorTile
              class="custom-color-picker__color-tile"
              :color="colorPickerState.isValid ? colorPickerState.hexColor : ''"
              size="large"
            />
          </template>
        </TextInput>
      </div>
      <div class="custom-color-picker__footer">
        <DarwinButton
          type="button"
          size="sm"
          flair="rounded"
          kind="outline"
          @click="emit('close')"
        >
          Close
        </DarwinButton>
        <DarwinButton
          type="submit"
          size="sm"
          flair="rounded"
          kind="primary"
          :disabled="!colorPickerState.isValid"
          @click="saveColor"
        >
          Save
        </DarwinButton>
      </div>
    </form>
  </CardSection>
</template>

<script setup lang="ts">
import { reactive, computed, ref, nextTick, defineExpose } from 'vue'

import CardSection from '@/uiKit/Card/CardSection.vue'
import TextInput from '@/uiKit/TextInput/TextInput.vue'
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import ColorTile from '@/uiKit/ColorPicker/ColorTile.vue'
import { isValidHexColor, ensureHexPrefix, anyToHex } from '@/uiKit/colorPalette'

const emit = defineEmits<{
  (e: 'save', color: string): void
  (e: 'close'): void
}>()

const props = withDefaults(
  defineProps<{
    color?: string
    hintText?: string
    headerText?: string
  }>(),
  {
    color: undefined,
    headerText: undefined,
    hintText: 'Choose a HEX code to customize your color.',
  },
)

const hexColor = computed(() => (props.color ? anyToHex(props.color) : ''))

const inputFieldRef = ref<InstanceType<typeof TextInput>>()
const colorPickerState = reactive({
  isValid: isValidHexColor(hexColor.value),
  hexColor: hexColor.value,
})

const validateHexColor = (value: string): void => {
  colorPickerState.isValid = isValidHexColor(value)
}

const saveColor = (): void => {
  if (colorPickerState.isValid) {
    emit('save', ensureHexPrefix(colorPickerState.hexColor))
  }
}

const setFocus = (): void => {
  nextTick(() => {
    inputFieldRef.value?.setFocus()
  })
}

defineExpose({
  setFocus,
})
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/spacings.scss';

.custom-color-picker {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: inherit;

  &.card__section {
    padding: 0;
  }

  &__header {
    padding: 10px;

    &-title {
      @include typographyRegularBody50;
      color: var(--color-text-subtle);
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1;
  }

  &__input-wrapper {
    padding: 5px;
  }

  &__input {
    :deep(.text-input__container) {
      background-color: var(--color-surface-secondary);
      padding-right: 0;
    }

    :deep(.text-input__container__icon--left) {
      margin-left: 0;
      margin-right: $spacing-4;
    }

    :deep(.text-input__container__input) {
      background-color: var(--color-surface-default);
      border-left: 1px solid var(--color-border-default);
      padding: 0 $spacing-3 0 10px;
      border-radius: 0 var(--size-2) var(--size-2) 0;
    }

    :deep(.text-input__label) {
      @include typographyRegularBody25;
      color: var(--color-text-subtlest);
      padding: $spacing-1 $spacing-2 $spacing-1 10px;
      text-align: left;
      white-space: normal;
    }
  }

  & &__color-tile {
    border-radius: $spacing-3;
  }

  &__footer {
    display: flex;
    gap: $spacing-1;
    padding: $spacing-2;
    border-top: 1px solid var(--color-border-default);

    :deep(.darwin-button) {
      width: 100%;
    }
  }
}
</style>
